import { useState } from 'react'

import { MenuOption } from '@joor/design-system'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import {
  AgGridFilterType,
  AgGridFilters,
  AgGridFiltersTypes,
  DateFilter,
  FilterDef,
  OptionProps,
  SetFilter,
  ValuesData,
} from '../agGridFilters.types'
import {
  getDateFilterOptions,
  getNumberFilterOptions,
  getSetFilterOptions,
  getTextFilterOptions,
} from '../agGridFilters.utils'
import { FilterSearchBar } from './FilterSearchBar/FilterSearchBar'
import { MULTIPLE_VALUE_TEXT } from './SetFilter/SetFilter'

// This value is the calculated height of the searchbar + 9 options
const FILTER_OPTIONS_MAX_HEIGHT = 372

type UseFilterMenuOptionsProps = {
  filterDefs: FilterDef[]
  selectedFilters: AgGridFilters<string>
  uniqueValuesByFilter: Record<string, string[]>
  valuesData: ValuesData
  handleUpdateFilters: (name: string, value: AgGridFiltersTypes) => void
  handleRemoveFilter: (name: string) => void
  hideSearch?: boolean
}

export const useFilterMenuOptions = ({
  filterDefs,
  selectedFilters,
  uniqueValuesByFilter,
  valuesData,
  handleUpdateFilters,
  handleRemoveFilter,
  hideSearch,
}: UseFilterMenuOptionsProps): OptionProps[] => {
  const [searchText, setSearchText] = useState('')
  const [filtersSearchText, setFiltersSearchText] = useState<
    Record<string, string>
  >({})

  const getOptionsByType = ({ id, name, type }: FilterDef): OptionProps[] => {
    switch (type) {
      case AgGridFilterType.Date:
        return getDateFilterOptions({
          name,
          value: selectedFilters[name] as DateFilter,
          onUpdateFilters: handleUpdateFilters,
          onRemoveFilter: handleRemoveFilter,
        })
      case AgGridFilterType.Text:
        return getTextFilterOptions({
          name,
          onUpdateFilters: handleUpdateFilters,
          onRemoveFilter: handleRemoveFilter,
        })
      case AgGridFilterType.Set:
        const uniqueValues = (uniqueValuesByFilter[name] ?? []).filter(
          (value) => !!value,
        )

        const options = uniqueValues.includes(MULTIPLE_VALUE_TEXT)
          ? [
              MULTIPLE_VALUE_TEXT,
              ...uniqueValues.filter((value) => value !== MULTIPLE_VALUE_TEXT),
            ]
          : uniqueValues

        return getSetFilterOptions({
          name,
          options: options.filter((value) => value !== EMPTY_CELL_CONTENT),
          values: (selectedFilters[name] as SetFilter)?.values || [],
          searchText: filtersSearchText[name],
          setSearchText: (newSearchText: string) =>
            setFiltersSearchText({
              ...filtersSearchText,
              [name]: newSearchText,
            }),
          onUpdateFilters: handleUpdateFilters,
          onRemoveFilter: handleRemoveFilter,
        })
      case AgGridFilterType.Number:
        return getNumberFilterOptions({
          name,
          ...valuesData[id],
          onUpdateFilters: handleUpdateFilters,
          onRemoveFilter: handleRemoveFilter,
        })
      default:
        return []
    }
  }

  const searchOption = {
    content: (
      <FilterSearchBar
        placeholder="Search filter"
        setSearchText={setSearchText}
      />
    ),
  }

  const allOptions = filterDefs
    .filter(({ label }) =>
      label.toLowerCase().includes(searchText.toLowerCase()),
    )
    .map((filterDef) => ({
      content: <MenuOption>{filterDef.label}</MenuOption>,
      options: getOptionsByType(filterDef),
      maxHeight: FILTER_OPTIONS_MAX_HEIGHT,
    }))

  return hideSearch ? allOptions : [searchOption, ...allOptions]
}
